import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Analytics, Transaction } from '../../types/ApiTypes';
import type { TransactionData } from '../../types/TransactionDataType';
import SkeletonCard from '../components/SkeletonCard';
import SkeletonTable from '../components/SkeletonTable';
import StatCard from '../components/StatsCard';
import TransactionList from '../components/TransactionList';
import { useApi } from '../hooks/useApi';
import Dashboard from '../layout/Dashboard';
import {
	CardsGrid,
	Content,
	Heading,
	HeadingSub,
} from '../styles/OverviewStyle';
import { formatDate } from '../utils/dateHelpers';
import { formatCompactNumber, formatWithCommas } from '../utils/numberHelpers';

export default function Overview(): React.JSX.Element {
	const navigate = useNavigate();
	const { analytics: analyticsService, transaction: transactionService } = useApi();
	const [analytics, setAnalytics] = useState<Analytics | null>(null);
	const [transactions, setTransactions] = useState<Transaction[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const loadData = async () => {
			setLoading(true);
			const [analyticsRes, transactionsRes] = await Promise.all([
				analyticsService.getAnalytics(),
				transactionService.getTransactions({ pageNumber: 1, pageSize: 10 })
			]);
			if (analyticsRes.status === 'success' && analyticsRes.data) {
				setAnalytics(analyticsRes.data);
			}
			if (transactionsRes.status === 'success' && transactionsRes.data) {
				setTransactions(transactionsRes.data.transactions);
			}
			setLoading(false);
		};
		loadData();
	}, [analyticsService, transactionService]);

	const setSelected = (ref: string) => {
		navigate(`/transactions?txn=${ref}`)
	}

	const getChangeState = (change: string): boolean | undefined => {
		const num = parseFloat(change);
		if (num > 0) return true;
		if (num < 0) return false;
		return undefined;
	}

	const formatTransactionType = (type: string): string => {
		return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
	}

	const mapTransactions = (txns: Transaction[]): TransactionData => {
		return txns.map(t => {
			const statusMap: Record<string, 'Completed' | 'Pending' | 'Failed'> = {
				completed: 'Completed',
				pending: 'Pending',
				failed: 'Failed'
			};

			let asset = 'N/A';
			let rate = 'N/A';

			if (t.giftcardPurchase) {
				asset = t.giftcardPurchase.brandName;
				rate = `₦${formatWithCommas(t.giftcardPurchase.ratePerDollar)}`;
			} else if (t.giftcardSale) {
				asset = t.giftcardSale.type;
				rate = `₦${formatWithCommas(t.giftcardSale.ratePerDollar)}`;
			} else if (t.cryptoPurchase) {
				asset = t.cryptoPurchase.currency.toUpperCase();
				rate = `₦${formatWithCommas(t.cryptoPurchase.ratePerDollar)}`;
			} else if (t.cryptoSale) {
				asset = t.cryptoSale.currency.toUpperCase();
				rate = `₦${formatWithCommas(t.cryptoSale.ratePerDollar)}`;
			} else if (t.cryptoDeposit) {
				asset = t.cryptoDeposit.currency.toUpperCase();
				rate = `₦${formatWithCommas(t.cryptoDeposit.ratePerDollar)}`;
			}

			return {
				ref: t.transactionId,
				user: `@${t.username}`,
				type: formatTransactionType(t.type),
				asset,
				amount: `₦${formatWithCommas(t.amount)}`,
				rate,
				status: statusMap[t.status] || 'Pending',
				date: formatDate(t.date)
			};
		});
	}

	return (
		<Dashboard title="Overview" icon="solar:home-2-linear" active="overview">
			<Content>
				<Heading>Overview</Heading>
				<HeadingSub>Stay on top of everything in one glance</HeadingSub>
				<CardsGrid>
					{loading ? (
						<>{[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}</>
					) : analytics && (
						<>
							<StatCard title='Total volume' value={`₦${formatCompactNumber(analytics.totalVolumeLastMonth)}`} change={`${analytics.volumePercentageChange}%`} changePositive={getChangeState(analytics.volumePercentageChange)} meta='vs last month' />
							<StatCard title='Total withdrawals' value={`₦${formatCompactNumber(analytics.totalWithdrawalLastMonth)}`} change={`${analytics.withdrawalPercentageChange}%`} changePositive={getChangeState(analytics.withdrawalPercentageChange)} meta='vs last month' />
							<StatCard title='Pending giftcard sales' value={formatWithCommas(analytics.pendingGiftcardSales)} meta='Waiting for approval' link />
							<StatCard title='Pending KYC' value={formatWithCommas(analytics.pendingKyc)} meta='Waiting for verification' link />
							<StatCard title='Total users' value={formatWithCommas(analytics.totalUsers)} change={`${analytics.usersPercentageChange}%`} changePositive={getChangeState(analytics.usersPercentageChange)} meta='vs last month' />
							<StatCard title='Total Naira balance' value={`₦${formatCompactNumber(analytics.totalNairaBalance)}`} meta='Total user NGN holdings' />
							<StatCard title='Total USD balance' value={`$${formatWithCommas(analytics.totalUsdBalance)}`} meta='Total user USD holdings' />
							<StatCard title='Total crypto assets' value={`₦${formatCompactNumber(analytics.totalCryptoAssetValue)}`} meta='All crypto holdings value' />
						</>
					)}
				</CardsGrid>

				<h2 className="self-stretch justify-center text-neutral-900 text-lg font-semibold">Recent transactions</h2>
				<div className="flex items-center justify-end mb-4 mt-4">
					<button onClick={() => navigate('/transactions')} className="flex items-center gap-1.5 px-3 py-2 text-xs tracking-tight bg-white border border-[#E7E7E9] rounded-lg cursor-pointer hover:bg-[#F9F9FA] transition-colors">
						<span className='text-neutral-900 font-medium'>View all</span>
						<Icon icon='solar:arrow-right-bold' width={12} />
					</button>
				</div>

				{loading ? <SkeletonTable /> : <TransactionList data={mapTransactions(transactions)} onSelect={t => setSelected(t.ref)} />}
			</Content>
		</Dashboard>
	)
}
